// src/components/services/MerchantServices.jsx
import React from "react";
import { motion } from "framer-motion";
import { Link } from 'react-router-dom'; // Import Link
import {
    MdOutlinePayments,
    MdOutlineSecurity,
    MdOutlineSpaceDashboard,
    MdOutlineDescription,
    MdVerified,
    MdLocalAtm,
    MdOutlinePointOfSale,
    MdOutlineSupportAgent
} from "react-icons/md";
import { FaPhoneAlt, FaMoneyBillWave, FaPiggyBank, FaUniversalAccess, FaChartLine, FaStore, FaHandshake } from "react-icons/fa";


function MerchantServices() {
    const serviceName = "Merchant Services"; // Set the service name

    // Animation Variants (Keep or move to utils/animations.js)
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2, // Stagger animation of child elements
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 },
    };

    return (
        <section className="py-16 md:py-24 px-4 md:px-8 bg-gray-50 text-gray-700">
            <div className="container mx-auto">
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                >
                    <motion.h1
                        variants={itemVariants}
                        className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 md:mb-8 text-center"
                    >
                        Merchant Services
                    </motion.h1>
                    <motion.p
                        variants={itemVariants}
                        className="text-lg md:text-xl text-gray-600 mb-10 md:mb-12 text-center max-w-3xl mx-auto"
                    >
                        Empower your business to accept digital payments with Keziah Global Private
                        Limited's Merchant Services, in partnership with Fino Payments Bank.
                        We provide a comprehensive suite of solutions to help you process
                        transactions seamlessly, enhance customer experience, and grow your
                        business.
                    </motion.p>

                    {/* Features and Benefits */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Key Features
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                            {/* Feature 1 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlinePointOfSale className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        POS Terminals
                                    </h4>
                                    <p className="text-gray-600">
                                        Accept payments through debit cards, credit cards, and
                                        prepaid cards with our secure and reliable POS terminals.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 2 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.1 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlinePayments className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        mPOS Solutions
                                    </h4>
                                    <p className="text-gray-600">
                                        Turn your smartphone into a mobile point-of-sale device for
                                        on-the-go payments.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 3 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.2 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <FaMoneyBillWave className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Payment Gateway Integration
                                    </h4>
                                    <p className="text-gray-600">
                                        Accept online payments securely through our integrated payment
                                        gateway.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 4 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.3 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlineDescription className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Easy Reconciliation
                                    </h4>
                                    <p className="text-gray-600">
                                        Simplify your accounting with easy reconciliation of all
                                        your transactions.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 5 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.4 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <FaChartLine className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Real-time Transaction Monitoring
                                    </h4>
                                    <p className="text-gray-600">
                                        Track your sales and transactions in real-time through our
                                        merchant dashboard.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 6 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.5 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlineSupportAgent className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Dedicated Support
                                    </h4>
                                    <p className="text-gray-600">
                                        Our support team is available to assist you with any
                                        questions or issues.
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>

                    {/* Why Choose Keziah for Merchant Services */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Why Choose Keziah for Merchant Services
                        </h3>
                        <ul className="list-disc pl-8 space-y-4 text-gray-600 text-lg">
                            <li>
                                Partnership with Fino Payments Bank, ensuring secure and
                                reliable payment processing.
                            </li>
                            <li>
                                Competitive transaction rates and transparent pricing.
                            </li>
                            <li>
                                Wide range of payment acceptance solutions to suit your business
                                needs.
                            </li>
                            <li>
                                Easy onboarding and quick setup.
                            </li>
                            <li>
                                Dedicated support to help you every step of the way.
                            </li>
                        </ul>
                    </motion.div>

                    {/* Call to Action */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Get Started with Merchant Services Today!
                        </h3>
                        <p className="text-gray-600 text-lg text-center">
                            Visit our branch in Salem, Tamil Nadu, to discuss your merchant
                            services needs and explore how we can help you grow your business.
                            You can also request a callback to learn more from one of our
                            experts.
                        </p>
                        <motion.div
                            variants={itemVariants}
                            className="mt-10 flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-6"
                        >
                            <a
                                href="tel:9043588446"
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-full text-base md:text-lg transition duration-300 ease-in-out shadow-md hover:shadow-lg text-center w-full md:w-auto flex items-center justify-center"
                                whileHover={{ scale: 1.05 }}
                            >
                                <FaPhoneAlt className="inline-block mr-2" />
                                Call Now
                            </a>
                            {/* Use Link for Callback */}
                            <Link to={`/callback?serviceName=${encodeURIComponent(serviceName)}`}>
                                <motion.button
                                    className="bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-full text-base md:text-lg transition duration-300 ease-in-out shadow-md hover:shadow-lg text-center w-full md:w-auto flex items-center justify-center"
                                    whileHover={{ scale: 1.05 }}
                                >
                                    Request a Callback
                                </motion.button>
                            </Link>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}

export default MerchantServices;